import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Ticket, Stats } from '../lib/types';
import { useApiResource } from '../hooks/useApiResource';
import { useWorkspaceTicket } from '../hooks/useWorkspaceTicket';
import { useWorkspaceActions } from '../hooks/useWorkspaceActions';
import { useSampleWorkspace } from '../hooks/useSampleWorkspace';
import { Action, PageHeading, Loading } from '../components/Common';
import { IncomingPanel } from '../components/IncomingPanel';
import { DraftPanel } from '../components/DraftPanel';
import { SourcesPanel } from '../components/SourcesPanel';
import { SampleDialog } from '../components/SampleDialog';
import { WorkspaceStats, RecentConversations } from '../components/WorkspaceOverview';

export default function Workspace() {
  const {id} = useParams();
  const state = useWorkspaceTicket(id);
  const stats = useApiResource<Stats | null>('/stats', null);
  const recent = useApiResource<Ticket[]>('/tickets', []);
  const {refresh: refreshStats} = stats;
  const {refresh: refreshRecent} = recent;
  const refresh = useCallback(async () => {
    await Promise.all([refreshStats(), refreshRecent()]);
  }, [refreshStats, refreshRecent]);
  const actions = useWorkspaceActions(id, state, refresh);
  const sample = useSampleWorkspace(state, refresh);
  const error = actions.error || state.error || stats.error || recent.error;
  const retry = () => {
    actions.setError('');
    void refresh();
    if (state.error) void state.refreshTicket();
  };
  const unavailable = Boolean(id && !state.ticket && state.error);
  if (state.loading) return <Loading/>;

  return <div className="workspace-page page-enter">
    <PageHeading eyebrow="A LITTLE ORDER. A LOT MORE CARE." title="Triage workspace" subtitle={state.ticket ? `${state.ticket.number}  /  ${state.ticket.title}` : 'Every conversation deserves a thoughtful reply.'} action={<Action variant="outline" data-testid="new-ticket-button" onClick={actions.newTicket} disabled={actions.busy}><Plus size={16}/>New ticket</Action>}/>
    {error && <div className="page-error" role="alert" data-testid="workspace-error">{error}<button data-testid="workspace-retry-button" onClick={retry}>Retry</button></div>}
    <WorkspaceStats stats={stats.data}/>
    {!unavailable && <div className="workbench">
      <IncomingPanel {...state} {...actions} onGenerate={() => actions.generate()} onSample={() => sample.setOpen(true)}/>
      <DraftPanel {...state} {...actions} onPatch={actions.patch} onSave={() => actions.patch({draft: state.draft})} onApprove={actions.approve} onRegenerate={() => actions.generate(true)}/>
      <SourcesPanel ticket={state.ticket} articles={stats.data?.articles || 0}/>
    </div>}
    <RecentConversations tickets={recent.data} total={stats.data?.total || 0}/>
    <SampleDialog open={sample.open} setOpen={sample.setOpen} busy={sample.busy} onLoad={sample.loadSample}/>
  </div>;
}